import React, { useEffect, useRef, useState } from "react";
import { ClipboardPaste, Loader2 } from "lucide-react";
import { Modal } from "components/ui/modal";
import { useI18n } from "i18n";

/**
 * Modal da busca por selfie: a imagem chega por arrastar e soltar, pelo
 * seletor de arquivo ou colada da area de transferencia (Ctrl/⌘+V ou o botao
 * "colar"). Nao ha campo de URL de proposito — o backend nunca busca imagem
 * remota em nome do visitante.
 *
 * `onSelect(file)` devolve uma Promise: enquanto ela corre o modal mostra o
 * carregamento; quem chama decide fechar (sucesso) ou passar `error`.
 */
export default function SelfieSearchModal({ open, onClose, onSelect, busy, error }) {
  const { t } = useI18n();
  const fileInput = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (open) {
      setLocalError("");
      setDragging(false);
    }
  }, [open]);

  const pick = (file) => {
    if (busy) return;
    if (!file || !file.type?.startsWith("image/")) {
      setLocalError(t("public.selfie.notImage"));
      return;
    }
    setLocalError("");
    onSelect(file);
  };

  // Ctrl/⌘+V em qualquer ponto da pagina enquanto o modal esta aberto.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) =>
        i.type.startsWith("image/")
      );
      if (!item) return;
      e.preventDefault();
      pick(item.getAsFile());
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });

  // Botao "colar": a Clipboard API async (pede permissao ao navegador). Sem
  // ela — Firefox, contexto inseguro — o atalho de teclado continua valendo.
  const pasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find((ty) => ty.startsWith("image/"));
        if (type) {
          const blob = await item.getType(type);
          pick(new File([blob], `clipboard.${type.split("/")[1]}`, { type }));
          return;
        }
      }
      setLocalError(t("public.selfie.clipboardEmpty"));
    } catch {
      setLocalError(t("public.selfie.clipboardDenied"));
    }
  };

  const message = localError || error;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={t("public.selfie.title")}
      size="xl"
    >
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files?.[0]);
        }}
        className={`mt-3 flex min-h-[260px] flex-col items-center justify-center gap-5 rounded-2xl border-2 border-dashed px-6 py-10 transition-colors ${
          dragging
            ? "border-amber-500 bg-amber-50"
            : "border-neutral-300 bg-neutral-50"
        }`}
      >
        {busy ? (
          <div className="flex flex-col items-center gap-3 text-neutral-600">
            <Loader2 size={32} className="animate-spin text-amber-500" />
            <span className="text-sm">{t("public.selfie.searching")}</span>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <DropImageIcon />
              <p className="text-base text-neutral-600">
                {t("public.selfie.drag")}{" "}
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-800"
                >
                  {t("public.selfie.upload")}
                </button>
              </p>
            </div>

            <div className="flex w-full items-center gap-4 text-xs font-medium uppercase text-neutral-400">
              <span className="h-px flex-1 bg-neutral-200" />
              {t("public.selfie.or")}
              <span className="h-px flex-1 bg-neutral-200" />
            </div>

            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={pasteFromClipboard}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100"
              >
                <ClipboardPaste size={16} />
                {t("public.selfie.paste")}
              </button>
              <span className="text-xs text-neutral-500">
                {t("public.selfie.pasteHint")}
              </span>
            </div>
          </>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      {message && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left text-sm text-red-700">
          {message}
        </p>
      )}

      <p className="mt-4 text-xs text-neutral-500">{t("public.selfie.privacy")}</p>
    </Modal>
  );
}

/** Ilustracao do dropzone: a moldura de recorte com a imagem por cima. */
function DropImageIcon() {
  return (
    <svg
      width="59"
      height="45"
      viewBox="0 0 59 45"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M40.3332 13.747L1.58323 13.747L1.58323 43.4553L40.3332 43.4553L40.3332 13.747Z" fill="#F1F3F4" />
      <path d="M40.3332 13.747L17.0832 13.747L17.0832 33.122L40.3332 33.122L40.3332 13.747Z" fill="#E8EAED" />
      <path fillRule="evenodd" clipRule="evenodd" d="M0.614479 12.7783L6.74988 12.7783L6.74988 14.7158L2.55198 14.7158L2.55198 18.9137L0.614479 18.9137L0.614479 12.7783Z" fill="#BDC1C6" />
      <path fillRule="evenodd" clipRule="evenodd" d="M39.3644 42.4866L39.3644 38.2887L41.3019 38.2887L41.3019 44.4241L35.1665 44.4241L35.1665 42.4866L39.3644 42.4866Z" fill="#BDC1C6" />
      <path fillRule="evenodd" clipRule="evenodd" d="M0.614479 38.2887L2.55198 38.2887L2.55198 42.4866L6.74987 42.4866L6.74987 44.4241L0.614479 44.4241L0.614479 38.2887Z" fill="#BDC1C6" />
      <path d="M19.6665 30.2531H58.4165L58.4165 0.544722H19.6665L19.6665 30.2531Z" fill="#AECBFA" />
      <path d="M19.6665 21.8429L19.6665 30.2525L58.4168 30.2525L58.4168 19.7406L49.6667 12.4069C48.6234 11.5342 47.2931 11.0699 45.9272 11.1018C44.5614 11.1337 43.2547 11.6596 42.2542 12.5801L33.4166 20.7918L28.4166 17.2548C27.7332 16.7781 26.9013 16.5563 26.0684 16.6288C25.2354 16.7012 24.4554 17.0632 23.8666 17.6505L19.6665 21.8429Z" fill="#669DF6" />
      <path d="M30.0056 12.9386C31.7315 12.9386 33.1306 11.5395 33.1306 9.8136C33.1306 8.08773 31.7315 6.68863 30.0056 6.68863C28.2798 6.68863 26.8807 8.08773 26.8807 9.8136C26.8807 11.5395 28.2798 12.9386 30.0056 12.9386Z" fill="#E8F0FE" />
    </svg>
  );
}
